import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { inRange } from 'lodash';
import { BiChevronRight } from '@react-icons/all-files/bi/BiChevronRight';
import { BiChevronLeft } from '@react-icons/all-files/bi/BiChevronLeft';

interface PaginationProps {
  total: number;
  current: number;
  className?: string;
  onChange: (page: number) => void;
}

function End(props: {
  className?: string;
  children?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <div className={classNames('-mt-px w-0 flex-1 flex', props.className)}>
      <button
        type="button"
        onClick={props.onClick}
        className="border-b-2 border-transparent pb-2 pl-1 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
      >
        {props.children}
      </button>
    </div>
  );
}

function Page(props: {
  number: number;
  active?: boolean;
  onChange: (page: number) => void;
}) {
  return (
    <button
      type="button"
      data-test-id={`pagination-page-${props.number}`}
      onClick={() => props.onChange?.(props.number)}
      className={classNames(
        'border-transparent border-b-2 pb-2 px-4 inline-flex items-center text-sm font-medium focus focus:outline-none',
        'hover:border-gray-300',
        props.active
          ? '!border-blue text-blue'
          : 'text-gray-500 hover:text-gray-700 hover:border-gray-300',
      )}
    >
      {props.number}
    </button>
  );
}

export function Pagination(props: PaginationProps) {
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    // the total from the endpoints meta properties would
    // reset every fetch, this will keep the total count correct
    if (props.total && props.total !== totalPages) {
      setTotalPages(props.total);
    }
  }, [props.total, totalPages]);

  return (
    <nav
      className={classNames(
        'px-4 flex items-center justify-between w-full sm:px-0',
        props.className,
      )}
    >
      <End
        onClick={() => props.onChange(props.current - 1)}
        className={classNames(
          'ml-3',
          props.current === 1 && 'opacity-0 pointer-events-none',
        )}
      >
        <BiChevronLeft size={30} className="mr-3 h-4 w-4" aria-hidden="true" />
        Previous
      </End>
      <div className="hidden md:-mt-px md:flex">
        {new Array(totalPages).fill(null).map((_, i) => {
          const page = i + 1;
          if (
            page === totalPages ||
            page === 1 ||
            (props.current < 4 && page < 5) ||
            (props.current > totalPages - 3 && page > totalPages - 4) ||
            inRange(page, props.current - 1, props.current + 2)
          ) {
            return (
              <Page
                key={page}
                active={page === props.current}
                number={page}
                onChange={props.onChange}
              />
            );
          }
          return page === 2 || page === totalPages - 1 ? '...' : null;
        })}
      </div>
      <End
        onClick={() => props.onChange(props.current + 1)}
        className={classNames(
          'justify-end mr-4',
          props.current === totalPages && 'opacity-0 pointer-events-none',
        )}
      >
        Next
        <BiChevronRight size={30} className="ml-3 h-4 w-4" aria-hidden="true" />
      </End>
    </nav>
  );
}
